import React from "react";
import { ICON_SIZE, ICON_STROKE } from "./tokens";
import { Info } from "lucide-react";

const cx = (...parts) => parts.filter(Boolean).join(" ");

/**
 * Lucide icon wrapper — enforces sm/md/lg size + stroke.
 * Usage: <DsIcon icon={Download} size="sm" />
 */
export function DsIcon({ icon: Icon, size = "md", className = "", strokeWidth = ICON_STROKE, ...rest }) {
  if (!Icon) return null;
  const px = ICON_SIZE[size] || ICON_SIZE.md;
  return (
    <Icon
      size={px}
      strokeWidth={strokeWidth}
      className={cx("shrink-0", className)}
      aria-hidden={rest["aria-label"] ? undefined : true}
      {...rest}
    />
  );
}

const btnBase =
  "ds-btn inline-flex items-center justify-center gap-2 font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-45";

const btnVariants = {
  primary:
    "border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))]",
  secondary:
    "border border-[hsl(var(--ds-secondary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-secondary)/0.08)] hover:border-[hsl(var(--ds-secondary-hover))] hover:text-[hsl(var(--ds-secondary-hover))]",
  outline:
    "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]",
  ghost: "bg-transparent text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]",
  danger:
    "border border-[hsl(var(--ds-danger))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.08)] hover:border-[hsl(var(--ds-danger-hover))] hover:text-[hsl(var(--ds-danger-hover))]",
};

const btnSizes = {
  sm: "h-8 rounded-md px-3 text-xs",
  md: "h-9 rounded-md px-3.5 text-sm",
  lg: "h-10 rounded-lg px-4 text-sm",
};

export const DsButton = React.forwardRef(function DsButton(
  {
    variant = "primary",
    size = "md",
    className = "",
    type = "button",
    loading = false,
    disabled,
    children,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(btnBase, btnVariants[variant] || btnVariants.primary, btnSizes[size] || btnSizes.md, className)}
      {...rest}
    >
      {children}
    </button>
  );
});

export function DsCard({ className = "", padding = "md", children, ...rest }) {
  const pad = padding === "sm" ? "p-4" : padding === "lg" ? "p-6" : "p-5";
  return (
    <div
      className={cx(
        "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]",
        pad,
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function DsPageHeader({ title, description,infoTooltip, actions, className = "" }) {
  return (
    <div className={cx("mb-6 flex flex-wrap items-start justify-between gap-4", className)}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="ds-h2 text-[hsl(var(--ds-fg))]">{title}</h1>
            {infoTooltip ? (
                <div className="group relative">
                  <Info
                    size={16}
                    className="cursor-pointer text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                  />

                  <div className="pointer-events-none absolute left-full top-full z-50 mt-2 hidden w-80 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-black shadow-lg group-hover:block">
                    {infoTooltip}
                  </div>
                </div>
              ) : null}
        </div>
        {description ? (
          <p className="ds-body mt-1 text-[hsl(var(--ds-muted-fg))]">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function DsLabel({ htmlFor, children, className = "" }) {
  return (
    <label
      htmlFor={htmlFor}
      className={cx("ds-caption mb-1.5 block text-[hsl(var(--ds-fg))]", className)}
    >
      {children}
    </label>
  );
}

export const DsInput = React.forwardRef(function DsInput(
  { className = "", darkMode, ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      className={cx(
        "ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none placeholder:text-[hsl(var(--ds-muted-fg))] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]",
        darkMode ? "dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))]" : "",
        className
      )}
      {...rest}
    />
  );
});

export function DsBadge({ tone = "neutral", children, className = "" }) {
  const tones = {
    neutral: "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]",
    primary: "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]",
    success: "bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]",
    warning: "bg-[hsl(var(--ds-warning)/0.18)] text-[hsl(var(--ds-warning-fg))]",
    danger: "bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))]",
    info: "bg-[hsl(var(--ds-info)/0.12)] text-[hsl(var(--ds-info))]",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        tones[tone] || tones.neutral,
        className
      )}
    >
      {children}
    </span>
  );
}

export function DsAlert({ tone = "danger", children, className = "" }) {
  const tones = {
    danger: "border-[hsl(var(--ds-danger)/0.35)] bg-[hsl(var(--ds-danger)/0.08)] text-[hsl(var(--ds-danger))]",
    warning: "border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)] text-[hsl(var(--ds-warning-fg))]",
    success: "border-[hsl(var(--ds-success)/0.35)] bg-[hsl(var(--ds-success)/0.08)] text-[hsl(var(--ds-success))]",
    info: "border-[hsl(var(--ds-info)/0.35)] bg-[hsl(var(--ds-info)/0.08)] text-[hsl(var(--ds-info))]",
  };
  return (
    <div className={cx("rounded-lg border px-4 py-3 text-sm", tones[tone] || tones.danger, className)}>
      {children}
    </div>
  );
}

export function DsSpinner({ className = "" }) {
  return (
    <div
      className={cx(
        "h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--ds-border))] border-t-[hsl(var(--ds-primary))]",
        className
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

export function DsEmptyState({ title, description, className = "" }) {
  return (
    <div
      className={cx(
        "mx-auto max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-6 text-center shadow-[var(--ds-shadow-card)]",
        className
      )}
    >
      <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">{title}</h4>
      {description ? <p className="ds-body mt-2 text-[hsl(var(--ds-muted-fg))]">{description}</p> : null}
    </div>
  );
}
